import get from 'lodash/get';

import Format from 'app/src/data/Format';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import PaymentModel from 'app/src/data/models/Payment';
import { BaseStripeTerminal } from 'app/src/payments/terminals/BaseStripeTerminal';
import StripeTerminalService, {
  StripeTerminalPaymentStatus,
  UnhandledStripeServiceError,
} from 'app/src/payments/terminals/StripeTerminalService';
import Terminal from 'app/src/payments/terminals/Terminal';
import { hasFeature } from 'root/api/admin/features';

export default class LocalStripeTerminal extends BaseStripeTerminal {
  static _MAXIMUM_POLLING_TIME_CLIENT_SIDE = 50000; // 50 seconds
  static _MAXIMUM_POLLING_TIME_BEFORE_RESOLVE = 12000; // 12 seconds
  constructor(terminalModel) {
    super(terminalModel, undefined, LocalStripeTerminal._MAXIMUM_POLLING_TIME_CLIENT_SIDE);
  }

  async getCardToken(paymentOptions) {
    this.stripeTerminalServiceInstance = await StripeTerminalService.getInstance();

    // We try to discover the reader first before staging a payment
    // to avoid creating a payment for nothing.
    if (this.hasMonitoringModule) {
      this.stripeTerminalServiceInstance.setTerminalMonitoringInstance(this.lspayMonitoring);
    }
    const selectedReader = await this._connectReader(paymentOptions.gatewayConfig);

    let cardToken;
    if (paymentOptions.isUnreferencedRefund) {
      // For unreferenced refunds, we need to obtain a Payment Method ID which we send to
      // the API when staging the payment
      cardToken = await this._processUnreferencedRefund(
        paymentOptions.gatewayConfig,
        selectedReader
      );
    }
    return cardToken;
  }

  /**
   * Communicates with the terminal to start a transaction.
   *
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount  The amount to transact with the terminal.
   * @param {Number} paymentOptions.saleID   The ID of the sale to which the transaction will be associated
   * @param {GatewayConfig} paymentOptions.gatewayConfig  The gateway configuration used to transact the request
   * @param {Boolean} paymentOptions.isManualEntry  Optional - when true the terminal will be prompt for a manual
   *                                 entry (when available)
   * @param {Boolean} paymentOptions.isUnreferencedRefund Optional - when true the unreferenced refund flow will be
   *                                                      triggered
   */
  async processPayment(paymentOptions = {}) {
    const cardToken = await this.getCardToken(paymentOptions);
    return super.processPayment({
      ...paymentOptions,
      cardToken,
      integrationType: PaymentModel.IntegrationType.CLIENT,
    });
  }

  /**
   * Cancels payment at the Pay-Service level or Stripe Terminal level
   * Rules for Cancelling at Pay-service level:
   * we will try to cancel the payment at pay service level.
   * After we run keepPolling we will receive canceled action from payments, which will clear the state from stripe
   * @returns a Promise that resolves to an empty object once the command has been successfully canceled.
   * @return {error} If cancellation fails, the Promise resolves to an object with an error
   */
  async cancelPayment(payment) {
    await super.cancelPayment(payment);
    if (
      this.stripeTerminalServiceInstance.getPaymentStatus() ===
      StripeTerminalPaymentStatus.WAITING_FOR_INPUT
    ) {
      try {
        await this.stripeTerminalServiceInstance.cancelCollect();
      } catch (err) {
        throw new Error(
          `Something went wrong while trying to abort the payment. If the problem persists please contact support.`
        );
      }
    }
  }

  /**
   * Do the logic to connect to Stripe Reader
   */
  async _connectReader(gatewayConfig) {
    try {
      if (
        get(this.stripeTerminalServiceInstance.currentTerminal, 'serial_number', '') ===
          get(this.terminalModel, 'attributes.configuration.serialNumber', '') &&
        this.stripeTerminalServiceInstance.getConnectionStatus() === 'connected'
      ) {
        return this.stripeTerminalServiceInstance.currentTerminal;
      }

      this.stripeTerminalServiceInstance.gatewayConfig = gatewayConfig;

      let discoveredReaders;
      try {
        const discoverResponse = await this.stripeTerminalServiceInstance.discoverReaders(
          get(gatewayConfig, 'attributes.parameters')
        );
        discoveredReaders = discoverResponse?.discoveredReaders;
      } catch (e) {
        this.lspayMonitoring?.sendEvent('DISCOVER', {
          payment: {
            status: 'ERROR',
            reason: e.error?.message ?? e.message,
          },
          ...(e.error instanceof Error && {
            error: {
              code: e.error.code,
              message: e.error.message,
              stack: e.error.stack,
            },
          }),
        });
        throw e;
      }

      const [readerFound] = discoveredReaders.filter(
        (reader) =>
          reader.serial_number === get(this.terminalModel, 'attributes.configuration.serialNumber')
      );

      if (!readerFound) {
        throw {
          code: UnhandledStripeServiceError.reader_register_error,
          message: `The payment terminal is not fully registered to this shop.\n Please, retry the pairing process under:\n Settings > Payment Processing > Configure.\n If you still experience the issue after the re-pairing process,\n please contact the support team.`,
        };
      }

      if (this.stripeTerminalServiceInstance.getConnectionStatus() !== 'not_connected') {
        this._disconnectReader();
      }

      await this.stripeTerminalServiceInstance.connectReader(readerFound);

      return this.stripeTerminalServiceInstance.currentTerminal;
    } catch (err) {
      throw err;
    }
  }

  /**
   * Disconnect any Stripe Reader currently connected to
   * the current Instance of the Stripe Terminal Service.
   */
  _disconnectReader() {
    this.stripeTerminalServiceInstance.disconnectReader();
  }

  pollApiForPaymentStatus(
    payment,
    pollingTimeBeforeFirstResolve = LocalStripeTerminal._MAXIMUM_POLLING_TIME_BEFORE_RESOLVE
  ) {
    return super.pollApiForPaymentStatus(payment, pollingTimeBeforeFirstResolve);
  }

  /**
   * Helper function to retrieve the status of the payment from the API.  Once the status is no longer
   * pending the promise will be resolved or rejected accordingly.
   *
   * @param {Payment} payment     The payment whose status is currently unknown
   *                              when the status of the payment has been resolved.
   * @private
   */
  async _getPaymentStatus(payment) {
    const updatedPayment = await super._getPaymentStatus(payment);

    if (!updatedPayment) {
      return;
    }

    const paymentStatus = updatedPayment.get('status');

    if (
      paymentStatus === PaymentModel.Status.APPROVED ||
      paymentStatus === PaymentModel.Status.CANCELLED ||
      paymentStatus === PaymentModel.Status.DECLINED ||
      paymentStatus === PaymentModel.Status.ERROR
    ) {
      return updatedPayment;
    }

    if (paymentStatus === PaymentModel.Status.PENDING) {
      const paymentToken = get(
        updatedPayment.get('cardPaymentRequest'),
        'parameters.paymentToken',
        null
      );

      const amount = Format.parseMoney(get(updatedPayment.get('SalePayment'), 'amount'));

      if (!amount) {
        throw new Error(
          `Unable to process the current payment. The amount of the payment you are trying to process is invalid. You might want to start a new sale or contact support.`
        );
      }

      if (paymentToken && !this.terminalResolvedPayment) {
        const event = new CustomEvent(Terminal.TerminalEvents.PAYMENT_STARTED, {
          detail: { payment: updatedPayment },
        });
        window.dispatchEvent(event);

        try {
          if (this._isCardPaymentRequestRefund(paymentToken, amount)) {
            //
            // referenced refund
            //
            await this._processRefund(paymentToken, amount, updatedPayment);
          } else {
            //
            // typical payment
            //
            await this._processPayment(paymentToken);

            try {
              // If the gatewayConfig or the payment have v3, call capture
              const shouldCapture =
                get(this.gatewayConfig, 'attributes.parameters.platformVersion', '') ===
                  GatewayConfig.PLATFORM_VERSIONS.v3 ||
                get(payment, 'attributes.cardPaymentRequest.parameters.platformVersion', '') ===
                  GatewayConfig.PLATFORM_VERSIONS.v3 ||
                (await hasFeature('PaymentsCaptureFlow'));

              if (shouldCapture) {
                await updatedPayment.capturePayment();
              }
            } catch (err) {
              // We treat any errors that come from hasFeature as a false return
            }
          }
        } catch (err) {
          // If the error is unhandled by a webhook,
          // we cancel payment and throw the error to the UI
          if (this._isUnhandledStripeServiceError(err) && this._isCancelableError(err)) {
            updatedPayment.cancelReason = PaymentModel.CancelReasons.ERROR;
            await this.cancelPayment(updatedPayment);
          }

          throw err;
        }
      }

      return;
    }

    throw new Error('Unknown payment status');
  }
}
